import { apiGet, apiPost, apiPatch } from './client';

/**
 * Phase 73 (Plan 73-04) — frontend API client for the session-authed
 * `/api/bot` surface built in Plan 73-03 (+ the session status route added
 * here per that plan's SUMMARY hand-off note; see
 * `backend/src/api/schemas/botConnection.ts`). Every interface field below
 * mirrors the backend response schemas EXACTLY — fast-json-stringify strips
 * anything not enumerated there, so there is no unused field to type here.
 */

export type PatStatus = 'active' | 'expired' | 'revoked';

// Mirrors `sessionBotStatusResponse` in backend/src/api/schemas/botConnection.ts.
// `data` is `null` when the caller has never connected (D-13).
// `model` is `null` when the caller has never set a preference (D-3 — no
// sentinel string, the SDK default applies).
export interface BotConnectionStatus {
  status: string;
  connectedAt: string | null;
  patStatus: PatStatus | null;
  model: string | null;
}

// MKS-388 — mirrors `timeLoggingStatusResponse` in
// backend/src/api/schemas/botConnection.ts. `blocked` is true only for a
// Claude seat holder with no connected Monka, and only while the release flag
// is on; the backend answers false for everybody else.
export interface TimeLoggingStatus {
  blocked: boolean;
}

// Mirrors `nonceValidityResponse`. Phase 75 (Plan 75-03 / ADMIN-02):
// `killSwitchActive` mirrors the backend field — forced true (with
// `valid: false`) whenever the org kill switch is on, regardless of the
// nonce's own validity, so ConnectPage can distinguish "disabled by admin"
// from a plain expired/invalid link.
export interface NonceValidity {
  valid: boolean;
  slackUserId: string | null;
  killSwitchActive: boolean;
}

// Mirrors `connectResultResponse` (shared by /connect and /rotate).
export interface ConnectResult {
  status: string;
  tokenVerified: boolean;
}

// quick-260817-hpp — mirrors `selfConnectResponse` in
// backend/src/api/schemas/botConnection.ts EXACTLY. `patMinted` tells the UI
// whether a new Monk PAT was created so it can invalidate the Personal API
// Tokens list meaningfully.
export interface SelfConnectResult {
  status: string;
  tokenVerified: boolean;
  patMinted: boolean;
}

// Mirrors `disconnectResponse`.
export interface DisconnectResult {
  success: boolean;
}

export interface ConnectInput {
  nonce: string;
  claudeToken: string;
}

export interface RotateInput {
  claudeToken: string;
}

export async function getBotStatus(): Promise<BotConnectionStatus | null> {
  const res = await apiGet<{ data: BotConnectionStatus | null }>('/bot/status');
  return res.data;
}

/**
 * MKS-388 — is the caller's time logging paused for a missing Monka
 * connection? Backs the `/time` banner, so the page can say so before the user
 * types an entry rather than only after the save comes back 403.
 */
export async function getTimeLoggingStatus(): Promise<TimeLoggingStatus> {
  const res = await apiGet<{ data: TimeLoggingStatus }>('/bot/time-logging-status');
  return res.data;
}

export async function getNonceValidity(nonce: string): Promise<NonceValidity> {
  const res = await apiGet<{ data: NonceValidity }>(`/bot/connect/${nonce}`);
  return res.data;
}

export async function connectBot(input: ConnectInput): Promise<ConnectResult> {
  const res = await apiPost<{ data: ConnectResult }>('/bot/connect', input);
  return res.data;
}

export async function disconnectBot(): Promise<DisconnectResult> {
  const res = await apiPost<{ data: DisconnectResult }>('/bot/disconnect', {});
  return res.data;
}

export async function rotateClaudeToken(input: RotateInput): Promise<ConnectResult> {
  const res = await apiPost<{ data: ConnectResult }>('/bot/rotate', input);
  return res.data;
}

/**
 * quick-260817-hpp — nonce-free connect/replace for the Profile page: paste
 * a `claude setup-token` to connect for the first time or replace an
 * existing one, without a Slack link. The `/bot/rotate` endpoint above stays
 * (it is simply no longer the Profile page's path).
 */
export async function selfConnectBot(input: { claudeToken: string }): Promise<SelfConnectResult> {
  const res = await apiPost<{ data: SelfConnectResult }>('/bot/self-connect', input);
  return res.data;
}

/**
 * PATCH /api/bot/model (Phase 77, Plan 77-02) — persists the caller's Monka
 * model preference. `model: null` clears the preference back to the SDK
 * default (D-3, locked — no sentinel string is ever sent).
 */
export async function updateBotModel(model: string | null): Promise<{ model: string | null }> {
  const res = await apiPatch<{ data: { model: string | null } }>('/bot/model', { model });
  return res.data;
}
